import { useState, useRef, useEffect } from "react";
import posthog, { captureError } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/enhanced-button";
import { Sparkles, Send, Bot, Square, AlertTriangle, BarChart3, TrendingUp } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { streamChatMessage, getChatHistory, setConversationTitle, abortChat, submitFeedback, regenerateAnswer } from "@/services/ai.service";
import type { FeedbackType, DislikeReason } from "@/services/ai.service";
import { ChatMessage } from "./ChatMessage";
import { ConversationSidebar, ConversationSidebarRef } from "./ConversationSidebar";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
}

const SELECTED_THREAD_KEY = "ai-mode-selected-thread";

interface AIModeProps {
  initialMessage?: string;
}

const INTENT_GROUPS = [
  {
    id: "diagnose",
    label: "Diagnose",
    Icon: AlertTriangle,
    colorClass: "text-amber-500",
    bgClass: "bg-amber-500/10",
    questions: [
      "What invoices are overdue?",
      "What's my burn rate this month?",
      "What bills do I need to pay this week?",
    ],
  },
  {
    id: "compare",
    label: "Compare",
    Icon: BarChart3,
    colorClass: "text-blue-500",
    bgClass: "bg-blue-500/10",
    questions: [
      "Compare revenue this month vs last month",
      "Which vendor am I paying the most to?",
      "How does cash burn compare to last quarter?",
    ],
  },
  {
    id: "forecast",
    label: "Forecast",
    Icon: TrendingUp,
    colorClass: "text-emerald-500",
    bgClass: "bg-emerald-500/10",
    questions: [
      "What's my cash runway?",
      "Forecast cash on hand 90 days out",
      "When will I need more funding?",
    ],
  },
];

const AIMode = ({ initialMessage }: AIModeProps = {}) => {
  const [inputValue, setInputValue] = useState(initialMessage ?? "");
  const [messages, setMessages] = useState<Message[]>([]);
  const [status, setStatus] = useState<"idle" | "streaming">("idle");
  const [threadId, setThreadId] = useState<string>(() => uuidv4());
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const conversationSidebarRef = useRef<ConversationSidebarRef>(null);
  const wasAbortedRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const justSwitchedConversationRef = useRef(false);

  // Auto-scroll: scroll to top on conversation switch, scroll to bottom during active chat
  useEffect(() => {
    if (justSwitchedConversationRef.current) {
      justSwitchedConversationRef.current = false;
      scrollContainerRef.current?.scrollTo({ top: 0 });
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: status === "streaming" ? "auto" : "smooth" });
  }, [messages, status]);

  const loadChatHistory = async (selectedThreadId: string) => {
    try {
      setIsLoadingHistory(true);
      const history = await getChatHistory(selectedThreadId);

      if (history && history.length > 0) {
        const loadedMessages: Message[] = history.map((msg, index) => {
          let content: string;
          if (typeof msg.content === 'string') {
            content = msg.content;
          } else if (msg.content && typeof msg.content === 'object') {
            const msgContent = msg.content as { content?: string; parts?: Array<{ text?: string }> };
            content = msgContent.content ||
                     msgContent.parts?.map((p) => p.text || String(p)).join('') ||
                     JSON.stringify(msg.content);
          } else {
            content = '';
          }
          return { id: `${selectedThreadId}-${index}`, role: msg.role, content };
        });
        setMessages(loadedMessages);
      } else {
        setMessages([]);
      }

      setThreadId(selectedThreadId);
      sessionStorage.setItem(SELECTED_THREAD_KEY, selectedThreadId);
    } catch (error) {
      console.error("Failed to load chat history:", error);
      captureError(error, { context: 'load_chat_history', thread_id: selectedThreadId });
      setMessages([]);
      setThreadId(selectedThreadId);
      sessionStorage.setItem(SELECTED_THREAD_KEY, selectedThreadId);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const sendMessage = async (userMessage: string, customThreadId?: string, isNewChat: boolean = false) => {
    const activeThreadId = customThreadId || threadId;
    const isFirstMessage = messages.length === 0 || isNewChat;

    posthog.capture("ai_question_submitted", { question: userMessage, thread_id: activeThreadId, source: customThreadId ? "example" : "input" });

    const userMsg: Message = { id: Date.now().toString(), role: "user", content: userMessage };
    setMessages(isNewChat ? [userMsg] : prev => [...prev, userMsg]);
    setStatus("streaming");
    wasAbortedRef.current = false;

    const assistantMsgId = (Date.now() + 1).toString();
    let accumulatedContent = '';
    let messageAdded = false;
    let rafScheduled = false;

    await streamChatMessage({
      threadId: activeThreadId,
      message: userMessage,
      onChunk: (content: string) => {
        accumulatedContent += content;
        if (!rafScheduled) {
          rafScheduled = true;
          requestAnimationFrame(() => {
            rafScheduled = false;
            if (!messageAdded) {
              setMessages(prev => [...prev, { id: assistantMsgId, role: "assistant", content: accumulatedContent }]);
              messageAdded = true;
            } else {
              setMessages(prev => prev.map(msg =>
                msg.id === assistantMsgId ? { ...msg, content: accumulatedContent } : msg
              ));
            }
          });
        }
      },
      onComplete: async () => {
        if (!messageAdded) {
          // Flush any content that arrived but whose RAF hasn't fired yet
          if (accumulatedContent) {
            setMessages(prev => [...prev, { id: assistantMsgId, role: "assistant", content: accumulatedContent }]);
            messageAdded = true;
          } else {
            setMessages(prev => [...prev, {
              id: assistantMsgId,
              role: "assistant",
              content: wasAbortedRef.current ? "Chat Stopped" : "No response from AI",
            }]);
          }
        }
        if (!wasAbortedRef.current && messageAdded) {
          posthog.capture("ai_response_viewed", { thread_id: activeThreadId });
        }
        setStatus("idle");

        if (isFirstMessage && !wasAbortedRef.current) {
          try {
            await setConversationTitle(activeThreadId);
            if (conversationSidebarRef.current) {
              await conversationSidebarRef.current.updateConversationTitle(activeThreadId);
            }
          } catch (error) {
            console.error('Failed to set conversation title:', error);
          }
        }
      },
      onError: (error: Error) => {
        console.error('Error sending message:', error);
        posthog.capture("ai_error", { thread_id: activeThreadId, error_message: error.message });
        captureError(error, { context: 'ai_chat', thread_id: activeThreadId });
        setMessages(prev => [...prev, {
          id: assistantMsgId,
          role: "assistant",
          content: "Sorry, I encountered an error processing your request.",
        }]);
        setStatus("idle");
      },
    });
  };

  const stopStreaming = async () => {
    wasAbortedRef.current = true;
    posthog.capture("ai_response_aborted", { thread_id: threadId, message_count: messages.length });
    setStatus("idle");
    try {
      await abortChat(threadId);
    } catch (error) {
      console.error('Error stopping stream:', error);
      captureError(error, { context: 'abort_chat', thread_id: threadId });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    sendMessage(inputValue);
    setInputValue("");
  };

  const handleQuickAction = (description: string) => {
    posthog.capture("ai_followup_clicked", { question: description });
    const newThreadId = uuidv4();
    setThreadId(newThreadId);
    sessionStorage.setItem(SELECTED_THREAD_KEY, newThreadId);
    sendMessage(description, newThreadId, true);
  };

  const handleFeedback = async (
    messageId: string,
    feedback: FeedbackType,
    dislikeReason?: DislikeReason,
    customReason?: string,
    regenerate?: boolean
  ) => {
    const msgIndex = messages.findIndex((m) => m.id === messageId);
    if (msgIndex === -1) return;
    const assistantMsg = messages[msgIndex];
    const questionMsg = messages.slice(0, msgIndex).reverse().find((m) => m.role === "user");
    const question = questionMsg?.content || "";
    const answer = assistantMsg.content;

    if (feedback === "dislike" && regenerate) {
      setStatus("streaming");
      wasAbortedRef.current = false;
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, content: "" } : m)));
      await regenerateAnswer({
        threadId,
        question,
        answer,
        dislikeReason,
        customReason,
        onChunk: (content) => {
          setMessages((prev) =>
            prev.map((m) => (m.id === messageId ? { ...m, content: m.content + content } : m))
          );
        },
        onComplete: () => setStatus("idle"),
        onError: () => setStatus("idle"),
      });
    } else {
      await submitFeedback({ threadId, question, answer, feedback, dislikeReason, customReason });
    }
  };

  const handleNewChat = () => {
    if (messages.length > 0) {
      posthog.capture("ai_conversation_ended", { thread_id: threadId, message_count: messages.length });
    }
    const newThreadId = uuidv4();
    setThreadId(newThreadId);
    setMessages([]);
    sessionStorage.setItem(SELECTED_THREAD_KEY, newThreadId);
  };

  const handleSelectConversation = async (selectedThreadId: string) => {
    if (selectedThreadId === threadId) return;
    if (messages.length > 0) {
      posthog.capture("ai_conversation_ended", { thread_id: threadId, message_count: messages.length });
    }
    justSwitchedConversationRef.current = true;
    await loadChatHistory(selectedThreadId);
  };

  const handleDeleteConversation = (deletedThreadId: string) => {
    if (deletedThreadId === threadId) {
      handleNewChat();
    }
  };

  return (
    <div className="flex h-full">
      {/* Conversation Sidebar — collapses to 56px, expands on hover */}
      <ConversationSidebar
        ref={conversationSidebarRef}
        selectedThreadId={threadId}
        onSelectConversation={handleSelectConversation}
        onNewChat={handleNewChat}
        onDeleteConversation={handleDeleteConversation}
      />

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* Messages / Empty State */}
        <div className="flex-1 overflow-hidden">
          <div ref={scrollContainerRef} className="h-full overflow-y-auto px-6 py-6">
            {messages.length === 0 ? (
              /* ── Empty State ─────────────────────────────────────────────── */
              <div className={cn(
                "flex flex-col items-center justify-center min-h-full gap-8 transition-opacity",
                isLoadingHistory ? "opacity-40 pointer-events-none" : "opacity-100"
              )}>
                {/* Icon + headline */}
                <div className="text-center">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Sparkles className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground">Ask anything about your books</h2>
                  <p className="text-muted-foreground text-sm mt-1.5">
                    Plain English. Real numbers. Backed by your live QuickBooks sync.
                  </p>
                </div>

                {/* 3 intent groups */}
                <div className="grid grid-cols-3 gap-4 w-full max-w-2xl">
                  {INTENT_GROUPS.map(({ id, label, Icon, colorClass, bgClass, questions }) => (
                    <div key={id} className="flex flex-col gap-2">
                      <div className="flex items-center gap-2 mb-0.5">
                        <div className={cn("w-6 h-6 rounded-md flex items-center justify-center", bgClass)}>
                          <Icon className={cn("w-3.5 h-3.5", colorClass)} />
                        </div>
                        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                          {label}
                        </span>
                      </div>
                      {questions.map(q => (
                        <button
                          key={q}
                          onClick={() => handleQuickAction(q)}
                          className="text-left text-sm px-3 py-2.5 rounded-lg bg-muted/40 hover:bg-muted border border-border/60 hover:border-border text-foreground transition-all duration-150 leading-snug"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* ── Active Chat ─────────────────────────────────────────────── */
              <div className="space-y-4 max-w-3xl mx-auto">
                {messages.map((message, index) => {
                  const isLast = index === messages.length - 1;
                  const isActivelyStreaming = isLast && status === "streaming" && message.role === "assistant";
                  return (
                    <ChatMessage
                      key={message.id}
                      message={message}
                      isStreaming={isActivelyStreaming}
                      onFeedback={message.role === "assistant"
                        ? (feedback, dislikeReason, customReason, regenerate) =>
                            handleFeedback(message.id, feedback, dislikeReason, customReason, regenerate)
                        : undefined
                      }
                    />
                  );
                })}
                {status === "streaming" && messages[messages.length - 1]?.role !== "assistant" && (
                  <div className="flex gap-3 justify-start">
                    <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                      <Bot className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div className="bg-secondary text-secondary-foreground rounded-lg px-4 py-3">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* ── Sticky Input ─────────────────────────────────────────────────── */}
        <div className="px-6 pb-5 pt-2 flex-shrink-0">
          <div className="bg-muted/50 backdrop-blur-sm rounded-full shadow-sm border border-border/60 px-3 py-1.5 max-w-3xl mx-auto">
            <form onSubmit={handleSubmit} className="flex items-center gap-2 h-8">
              <Sparkles className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <Input
                type="text"
                placeholder="Ask anything about your books..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="flex-1 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-sm h-8 p-0 placeholder:text-muted-foreground/60"
                disabled={status === "streaming"}
              />
              {status === "streaming" ? (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={stopStreaming}
                  className="rounded-full h-7 px-3 text-xs flex-shrink-0 gap-1.5"
                >
                  <Square className="w-3 h-3 fill-current" />
                  Stop
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="quickbooks"
                  disabled={!inputValue.trim()}
                  className="rounded-full h-7 px-4 text-sm flex-shrink-0 gap-1.5"
                >
                  <Send className="w-3.5 h-3.5" />
                  Send
                </Button>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIMode;
